/**
 * Utility functions for plain text results rendering.
 * Extracted for testability.
 */

import type { QueryResultSet } from '../../../../types/queryExecution';
import type { QueryMessage } from '../../core/types/workspaceTypes';

export const MAX_COLUMN_WIDTH = 50;
export const MIN_COLUMN_WIDTH = 4;

/**
 * A row-count line, i.e. "a result set just completed" — never user output.
 *
 * Covers what SQL Server itself sends and the SQL Tools Service route
 * forwards verbatim (`(1 row affected)`, `(100 rows affected)`) AND the
 * wording every engine adapter composes itself (`1 row(s) affected`).
 *
 * The adapters' own `row(s)` spelling used to fall through here, so on the
 * driver route every row-count line counted as user PRINT output: it raised
 * the Output tab on scripts that printed nothing, and — once PRINT text
 * actually started reaching the panes (2026-09-21) — it consumed the
 * `[Result N]` anchor slots, placing result sets one position too early.
 */
export const ROWS_AFFECTED_PATTERN = /^\(?\d+\s*rows?(?:\(s\))?\s*(?:affected|returned)?\)?\.?$/i;

/**
 * PostgreSQL and Oracle both state a command tag ahead of the count:
 * `INSERT: 3 row(s) affected` (`pgsqlAdapter`, `` `${result.command}: …` ``,
 * where `result.command` is node-postgres's libpq command tag) and
 * `UPDATE: 3 row(s) affected` (`oracleAdapter`, `` `${cmd}: …` ``, where
 * `cmd` is the upper-cased first whitespace-separated token of the user's
 * statement).
 *
 * Deliberately stricter than the pattern above — CASE-SENSITIVE prefix, a
 * mandatory `affected`/`returned`, AND an ALLOWLIST of the real command tags
 * those two adapters can actually emit (`COMMAND_PREFIX_KEYWORDS`), rather
 * than "any upper-case word(s)". An arbitrary upper-case word before a colon
 * is something a user's own `PRINT` / `DBMS_OUTPUT.PUT_LINE` line plausibly
 * says too — e.g. an Oracle progress log like `TOTAL: 3 row(s) affected` or
 * `STEP: 1 row(s) affected` — and misreading one as a row count would cost
 * it its place in the Output view. The allowlist is every libpq command tag
 * PostgreSQL can return plus the DML/DDL leading keywords Oracle's adapter
 * upper-cases from the user's statement.
 *
 * The trailing `(?: [A-Z]+)*` is defensive, not reachable today: both
 * adapters only ever emit a SINGLE leading word. `result.command` is
 * node-postgres's own first-word capture of the libpq tag (`pg/lib/result.js`'s
 * `matchRegexp` is `[A-Za-z]+`, so a "CREATE TABLE"-shaped tag surfaces as
 * just `CREATE`), and Oracle's `cmd` is only ever
 * `stmt.trim().split(/\s+/)[0]`. Neither adapter emits this message for DDL
 * at all, single word or not — `pgsqlAdapter` guards on
 * `result.rowCount !== null` and `oracleAdapter` on `result.rowsAffected > 0`,
 * and DDL never carries a row count on either engine.
 */
const COMMAND_PREFIX_KEYWORDS = [
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'TRUNCATE',
  'CREATE', 'DROP', 'ALTER', 'GRANT', 'REVOKE', 'COPY', 'MOVE', 'FETCH',
  'CALL', 'DO', 'SET', 'COMMENT', 'ANALYZE', 'VACUUM', 'REFRESH', 'REINDEX',
  'CLUSTER', 'EXPLAIN', 'WITH', 'VALUES', 'TABLE', 'LOCK', 'SAVEPOINT',
  'RELEASE', 'BEGIN', 'COMMIT', 'ROLLBACK', 'START', 'PREPARE', 'EXECUTE',
  'DEALLOCATE', 'LISTEN', 'NOTIFY', 'UNLISTEN', 'DECLARE', 'CLOSE', 'IMPORT',
];

const COMMAND_PREFIXED_ROWS_AFFECTED = new RegExp(
  `^(?:${COMMAND_PREFIX_KEYWORDS.join('|')})(?: [A-Z]+)*:\\s*\\d+\\s*rows?(?:\\(s\\))?\\s+(?:affected|returned)\\)?\\.?$`
);

// Messages to filter out from display
export const FILTER_PATTERNS = [
  /executing query/i,
  /query completed/i,
];

// Auto-generated info messages that look like PRINTs but are not user-emitted —
// emitted either by useQueryExecution wrapping the run, or by SQL Tools Service
// at end-of-batch. Kept distinct from user PRINTs so the interleaving heuristic
// (used when SET NOCOUNT ON suppresses the rows-affected anchors) does not pick
// them up as result-set positional anchors.
export const AUTO_MESSAGE_PATTERNS: RegExp[] = [
  /^Executing query/i,
  /^Query executed successfully/i,
  /^Cancelling query/i,
  /^Commands completed successfully/i,
];

/**
 * Check if a message indicates rows affected (result set completion marker)
 */
export function isRowsAffectedMessage(content: string): boolean {
  const trimmed = content.trim();
  return ROWS_AFFECTED_PATTERN.test(trimmed) || COMMAND_PREFIXED_ROWS_AFFECTED.test(trimmed);
}

/**
 * Check if a message should be filtered out
 */
export function shouldFilterMessage(content: string): boolean {
  return FILTER_PATTERNS.some((pattern) => pattern.test(content));
}

export function isAutoGeneratedInfo(content: string): boolean {
  return AUTO_MESSAGE_PATTERNS.some((p) => p.test(content.trim()));
}

export function isUserPrintMessage(message: QueryMessage): boolean {
  if (message.type !== 'info') return false;
  if (shouldFilterMessage(message.content)) return false;
  if (isRowsAffectedMessage(message.content)) return false;
  if (isAutoGeneratedInfo(message.content)) return false;
  return true;
}

export function hasUserPrintMessages(messages: QueryMessage[] | undefined): boolean {
  if (!messages || messages.length === 0) return false;
  return messages.some(isUserPrintMessage);
}

/**
 * Format a value for display in the plain text table
 */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Header label for a column — `Column N` when the engine reported no name
 * (`SELECT COUNT(*)` on SQL Server), the same fallback the grid, the
 * streaming CSV export, the notebook HTML export, and the clipboard
 * Copy as CSV/Markdown/HTML formatters apply.
 */
export function columnHeaderLabel(col: { name: string }, index: number): string {
  return col.name.trim().length > 0 ? col.name : `Column ${index + 1}`;
}

/**
 * Calculate the display width for each column based on header and content
 */
export function calculateColumnWidths(
  columns: Array<{ id?: string; name: string }>,
  rows: Record<string, unknown>[]
): number[] {
  return columns.map((col, index) => {
    let maxWidth = columnHeaderLabel(col, index).length;
    for (const row of rows) {
      const value = formatValue(row[col.id ?? col.name]);
      maxWidth = Math.max(maxWidth, value.length);
    }
    return Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, maxWidth));
  });
}

/**
 * Pad a string to a fixed width
 */
export function padCell(value: string, width: number): string {
  if (value.length > width) {
    return value.slice(0, width - 1) + '…';
  }
  return value.padEnd(width);
}

/**
 * Generate the separator line (+----+----+)
 */
export function generateSeparator(widths: number[]): string {
  return '+' + widths.map((w) => '-'.repeat(w + 2)).join('+') + '+';
}

/**
 * Generate a data row (| val | val |)
 */
export function generateRow(values: string[], widths: number[]): string {
  return '| ' + values.map((v, i) => padCell(v, widths[i])).join(' | ') + ' |';
}

/**
 * Format a message for plain text display
 */
export function formatMessage(message: QueryMessage): string {
  const prefix = message.type === 'error' ? 'Error: ' :
                 message.type === 'warning' ? 'Warning: ' :
                 '';
  return `${prefix}${message.content}`;
}

/**
 * Render a single result set as ASCII table (without row count - that comes from message)
 */
export function renderResultSet(resultSet: QueryResultSet): string {
  const columns = resultSet.columns ?? [];
  const rows = resultSet.rows ?? [];

  if (columns.length === 0) {
    return '(No columns)';
  }

  const widths = calculateColumnWidths(columns, rows);
  const lines: string[] = [];

  // Top border
  lines.push(generateSeparator(widths));

  // Header row
  const headerValues = columns.map(columnHeaderLabel);
  lines.push(generateRow(headerValues, widths));

  // Header separator
  lines.push(generateSeparator(widths));

  // Data rows
  for (const row of rows) {
    const rowValues = columns.map((col) => formatValue(row[col.id ?? col.name]));
    lines.push(generateRow(rowValues, widths));
  }

  // Bottom border
  lines.push(generateSeparator(widths));

  return lines.join('\n');
}

/**
 * Output item - can be either a message or a result set
 */
export type OutputItem =
  | { type: 'message'; message: QueryMessage }
  | { type: 'resultSet'; resultSet: QueryResultSet; index: number };

/**
 * Build an interleaved output stream based on message timestamps.
 *
 * The algorithm:
 * 1. Sort messages by timestamp
 * 2. Find "rows affected" messages which indicate result set boundaries
 * 3. Insert result sets before their corresponding "rows affected" messages
 * 4. Other messages (PRINT statements) appear in their timestamp order
 */
export function buildOrderedOutput(
  resultSets: QueryResultSet[] | undefined,
  messages: QueryMessage[] | undefined
): OutputItem[] {
  const output: OutputItem[] = [];

  // Filter and sort messages by timestamp
  const sortedMessages = [...(messages ?? [])]
    .filter((m) => !shouldFilterMessage(m.content))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (!resultSets || resultSets.length === 0) {
    // No result sets - just output messages
    for (const msg of sortedMessages) {
      output.push({ type: 'message', message: msg });
    }
    return output;
  }

  if (sortedMessages.length === 0) {
    // No messages - just output result sets
    for (let i = 0; i < resultSets.length; i++) {
      output.push({ type: 'resultSet', resultSet: resultSets[i], index: i });
    }
    return output;
  }

  // Interleave messages and result sets.
  //
  // Anchor strategy:
  //   - "(N rows affected)" markers — present in default-NOCOUNT scripts —
  //     are unambiguous positional anchors; place result set i before the
  //     i-th rows-affected message.
  //   - When SET NOCOUNT ON suppresses those markers (the user's actual repro
  //     script), fall back to user-PRINT messages as anchors. STS streams
  //     PRINTs in script order; placing result set i AFTER user-PRINT i is
  //     the closest faithful reconstruction available without per-statement
  //     selection metadata. Auto-generated info messages and STS post-batch
  //     "Commands completed successfully" are excluded so they don't consume
  //     anchor slots.
  let resultSetIndex = 0;
  const hasRowsAffectedAnchor = sortedMessages.some((m) => isRowsAffectedMessage(m.content));

  if (hasRowsAffectedAnchor) {
    for (const msg of sortedMessages) {
      if (isRowsAffectedMessage(msg.content) && resultSetIndex < resultSets.length) {
        output.push({ type: 'resultSet', resultSet: resultSets[resultSetIndex], index: resultSetIndex });
        resultSetIndex++;
      }
      output.push({ type: 'message', message: msg });
    }
  } else {
    for (const msg of sortedMessages) {
      output.push({ type: 'message', message: msg });
      if (isUserPrintMessage(msg) && resultSetIndex < resultSets.length) {
        output.push({ type: 'resultSet', resultSet: resultSets[resultSetIndex], index: resultSetIndex });
        resultSetIndex++;
      }
    }
  }

  // Trailing slots — when there are more result sets than anchors of the
  // chosen kind, append the remainder so nothing gets dropped.
  while (resultSetIndex < resultSets.length) {
    output.push({ type: 'resultSet', resultSet: resultSets[resultSetIndex], index: resultSetIndex });
    resultSetIndex++;
  }

  return output;
}

/**
 * Build the complete plain text output from result sets and messages
 */
export function buildPlainTextOutput(
  resultSets: QueryResultSet[] | undefined,
  messages: QueryMessage[] | undefined
): string {
  const orderedOutput = buildOrderedOutput(resultSets, messages);

  if (orderedOutput.length === 0) {
    return 'No results to display.';
  }

  const lines: string[] = [];

  for (const item of orderedOutput) {
    if (item.type === 'message') {
      lines.push(formatMessage(item.message));
    } else {
      // Add blank line before result set if there's content before it
      if (lines.length > 0 && lines[lines.length - 1] !== '') {
        lines.push('');
      }
      lines.push(renderResultSet(item.resultSet));
    }
  }

  return lines.join('\n');
}
